import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { retry, timeout } from 'rxjs/operators';
import { ITreatmentPlanDetailed, ITreatmentPlanPagedResponse, ITreatmentPlanPhoto, ITreatmentPlanProduct, ISendPdfEmailResponse, ISendPdfEmailOptions } from '../models/treatment-plan.model';
import { AuthService } from './auth.service';
import { IApplicationRegion, IPagedData } from '../models/treatment-plan.model';
import { GlobalConfigService } from './global-config.service';

export type TreatmentPhotoType = 'PRE_TREATMENT' | 'POST_TREATMENT' | 'MARKING' | 'IMMEDIATE_POST_TREATMENT';

@Injectable({
  providedIn: 'root'
})
export class TreatmentPlanService {
  private get baseUrl(): string {
    return this.globalConfig.getApiUrl() + '/treatment-plans';
  }

  constructor(
    private http: HttpClient,
    private globalConfig: GlobalConfigService
  ) { }


  /**
   * Obtém um plano de tratamento pelo ID.
   *
   * Timeout curto (6s) com retry em erro transiente (status 0 / TimeoutError).
   * Esse endpoint é o GET inicial do fluxo /upload-photos via QR — em mobile
   * o OPTIONS preflight pode travar intermitente. Cortamos cedo e retentamos
   * automaticamente em vez de fazer o paciente esperar 30s e ver erro.
   */
  getTreatmentPlanById(id: string): Promise<ITreatmentPlanDetailed> {
    return new Promise((resolve, reject) => {
      this.http.get<ITreatmentPlanDetailed>(`${this.baseUrl}/${id}`)
        .pipe(
          timeout(6000),
          retry({
            count: 2,
            delay: (error, retryCount) => {
              // Só retenta em network error (status 0) ou TimeoutError.
              // 4xx/5xx propagam sem retry — são problemas reais do backend.
              const isTransient =
                error?.status === 0 || error?.name === 'TimeoutError';
              if (!isTransient) {
                return throwError(() => error);
              }
              const backoffMs = retryCount === 1 ? 500 : 1500;
              return timer(backoffMs);
            }
          })
        )
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error)
        });
    });
  }

  /**
   * Lista todos os planos de tratamento com suporte a paginação e filtros
   */
  listAllTreatmentPlans(
    pageNumber: number = 0,
    pageSize: number = 10,
    status?: 'ACTIVE' | 'CLOSED',
    startDate?: string,
    endDate?: string,
    clientId?: string,
    search?: string
  ): Observable<IPagedData<ITreatmentPlanDetailed>> {
    let params = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;

    if (status) {
      params += `&status=${status}`;
    }

    // Formatar as datas para o padrão ISO 8601 completo e codificar para URL
    if (startDate) {
      // Adicionar T00:00:00 para a data inicial
      const formattedStartDate = encodeURIComponent(`${startDate}T00:00:00`);
      params += `&startDate=${formattedStartDate}`;
    }

    if (endDate) {
      // Adicionar T23:59:59 para a data final
      const formattedEndDate = encodeURIComponent(`${endDate}T23:59:59`);
      params += `&endDate=${formattedEndDate}`;
    }

    if (clientId) {
      params += `&clientId=${clientId}`;
    }

    if (search && search.trim()) {
      params += `&search=${encodeURIComponent(search.trim())}`;
    }

    return this.http.get<IPagedData<ITreatmentPlanDetailed>>(`${this.baseUrl}${params}`);
  }

  /**
   * Lista planos de tratamento associados a um grupo de respostas
   */
  listByAnswerGroup(answerGroupId: string): Promise<ITreatmentPlanDetailed[]> {
    return new Promise((resolve, reject) => {
      this.http.get<ITreatmentPlanDetailed[]>(`${this.baseUrl}/by-answer-group/${answerGroupId}`)
        .subscribe({
          next: (plans) => resolve(plans),
          error: (err) => reject(err)
        });
    });
  }

  /**
   * Cria um novo plano de tratamento
   */
  createTreatmentPlan(data: Partial<ITreatmentPlanDetailed>): Promise<ITreatmentPlanDetailed> {
    return new Promise((resolve, reject) => {
      this.http.post<ITreatmentPlanDetailed>(`${this.baseUrl}`, data)
        .subscribe({
          next: (plan) => resolve(plan),
          error: (err) => reject(err)
        });
    });
  }

  /**
   * Atualiza um plano de tratamento
   */
  updateTreatmentPlan(id: string, data: Partial<ITreatmentPlanDetailed>): Promise<ITreatmentPlanDetailed> {
    return new Promise((resolve, reject) => {
      this.http.put<ITreatmentPlanDetailed>(`${this.baseUrl}/${id}`, data)
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error)
        });
    });
  }

  /**
   * Remove um produto do plano de tratamento
   */
  removeProduct(treatmentPlanId: string, productId: string): Promise<void> {
    return this.getTreatmentPlanById(treatmentPlanId)
      .then(plan => {
        // Filtrar o produto específico
        const updatedProducts = plan.products.filter(p => p.id !== productId);

        // Atualizar o plano com o novo array de produtos e enviar o objeto completo
        return this.updateTreatmentPlan(treatmentPlanId, {
          ...plan,
          products: updatedProducts
        }).then(() => void 0);
      });
  }

  /**
   * Adiciona uma foto ao plano de tratamento
   */
  addPhoto(treatmentPlanId: string, photoData: { description: string, type: 'PRE' | 'POST' }, file: File): Promise<ITreatmentPlanPhoto> {
    return new Promise<ITreatmentPlanPhoto>((resolve, reject) => {
      // Converter o arquivo para base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remover o prefixo "data:image/jpeg;base64," para obter apenas o conteúdo base64
        const photo = base64.split(',')[1];

        // Preparar dados para API - garantindo que o tipo seja PRE ou POST
        const apiPhotoData: Partial<ITreatmentPlanPhoto> = {
          photo,
          type: photoData.type === 'PRE' ? 'PRE' : 'POST'
        };

        // Adicionar a foto ao plano de tratamento
        this.getTreatmentPlanById(treatmentPlanId)
          .then(plan => {
            // Determinar photo_order baseado na quantidade de fotos existentes
            apiPhotoData.photo_order = (plan.photos?.length || 0) + 1;

            const updatedPhotos = [...(plan.photos || []), apiPhotoData as ITreatmentPlanPhoto];

            // Enviar o plano completo com as fotos atualizadas
            return this.updateTreatmentPlan(treatmentPlanId, {
              ...plan,
              photos: updatedPhotos
            });
          })
          .then(updatedPlan => {
            // Encontrar a foto recém-adicionada (a última)
            const newPhoto = updatedPlan.photos[updatedPlan.photos.length - 1];

            // Adicionar campos do frontend
            const responsePhoto: ITreatmentPlanPhoto = {
              ...newPhoto,
              description: photoData.description,
              created_at: new Date().toISOString(),
              photo_url: `data:image/jpeg;base64,${newPhoto.photo}`
            };

            resolve(responsePhoto);
          })
          .catch(reject);
      };
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Remove uma foto do plano de tratamento
   */
  removePhoto(treatmentPlanId: string, photoId: string): Promise<void> {
    return this.getTreatmentPlanById(treatmentPlanId)
      .then(plan => {
        // Filtrar a foto específica
        const updatedPhotos = plan.photos.filter(p => p.id !== photoId);

        // Atualizar o plano com o novo array de fotos e enviar o objeto completo
        return this.updateTreatmentPlan(treatmentPlanId, {
          ...plan,
          photos: updatedPhotos
        }).then(() => void 0);
      });
  }

  /**
   * Faz upload de uma foto de produto
   */
  uploadProductPhoto(
    treatmentPlanId: string,
    productId: string,
    photoType: 'bottle' | 'box' | 'label',
    file: File
  ): Promise<{ url: string }> {
    return new Promise<{ url: string }>((resolve, reject) => {
      // Converter o arquivo para base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remover o prefixo "data:image/jpeg;base64," para obter apenas o conteúdo base64
        const photo = base64.split(',')[1];

        // Determinar ordem da foto baseado no tipo
        let photo_order = 0;
        switch (photoType) {
          case 'bottle': photo_order = 0; break;
          case 'box': photo_order = 1; break;
          case 'label': photo_order = 2; break;
        }

        // Preparar dados para API
        const apiPhotoData = {
          photo,
          photo_order
        };

        // Adicionar a foto ao produto no plano de tratamento
        this.getTreatmentPlanById(treatmentPlanId)
          .then(plan => {
            // Encontrar o produto
            const productIndex = plan.products.findIndex(p => p.id === productId);
            if (productIndex === -1) {
              throw new Error('Produto não encontrado');
            }

            // Adicionar/atualizar a foto do produto
            const updatedProducts = [...plan.products];

            // Inicializar o array de fotos se não existir
            if (!updatedProducts[productIndex].photos) {
              updatedProducts[productIndex].photos = [];
            }

            // Verificar se já existe uma foto com esta ordem
            const existingPhotoIndex = updatedProducts[productIndex].photos!.findIndex(
              p => p.photo_order === photo_order
            );

            if (existingPhotoIndex !== -1) {
              // Atualizar foto existente
              updatedProducts[productIndex].photos![existingPhotoIndex] = apiPhotoData;
            } else {
              // Adicionar nova foto
              updatedProducts[productIndex].photos!.push(apiPhotoData);
            }

            // Atualizar o plano com o novo array de produtos e enviar o objeto completo
            return this.updateTreatmentPlan(treatmentPlanId, {
              ...plan,
              products: updatedProducts
            });
          })
          .then(() => {
            // Retornar URL para visualização no frontend
            resolve({
              url: base64
            });
          })
          .catch(reject);
      };
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Faz upload de uma foto de produto usando string base64
   */
  uploadProductPhotoBase64(
    treatmentPlanId: string,
    productId: string,
    photoType: 'bottle' | 'box' | 'label',
    base64Content: string
  ): Promise<{ url: string }> {
    return new Promise<{ url: string }>((resolve, reject) => {
      // Determinar ordem da foto baseado no tipo
      let photo_order = 0;
      switch (photoType) {
        case 'bottle': photo_order = 0; break;
        case 'box': photo_order = 1; break;
        case 'label': photo_order = 2; break;
      }

      // Preparar dados para API
      const apiPhotoData = {
        photo: base64Content,
        photo_order
      };

      // Criar URL para preview no frontend
      const photoUrl = `data:image/jpeg;base64,${base64Content}`;

      // Adicionar a foto ao produto no plano de tratamento
      this.getTreatmentPlanById(treatmentPlanId)
        .then(plan => {
          // Encontrar o produto
          const productIndex = plan.products.findIndex(p => p.id === productId);
          if (productIndex === -1) {
            throw new Error('Produto não encontrado');
          }

          // Adicionar/atualizar a foto do produto
          const updatedProducts = [...plan.products];

          // Inicializar o array de fotos se não existir
          if (!updatedProducts[productIndex].photos) {
            updatedProducts[productIndex].photos = [];
          }

          // Verificar se já existe uma foto com esta ordem
          const existingPhotoIndex = updatedProducts[productIndex].photos!.findIndex(
            p => p.photo_order === photo_order
          );

          if (existingPhotoIndex !== -1) {
            // Atualizar foto existente
            updatedProducts[productIndex].photos![existingPhotoIndex] = apiPhotoData;
          } else {
            // Adicionar nova foto
            updatedProducts[productIndex].photos!.push(apiPhotoData);
          }

          // Atualizar o plano com o novo array de produtos e enviar o objeto completo
          return this.updateTreatmentPlan(treatmentPlanId, {
            ...plan,
            products: updatedProducts
          });
        })
        .then(() => {
          // Retornar URL para visualização no frontend
          resolve({
            url: photoUrl
          });
        })
        .catch(reject);
    });
  }

  /**
   * Publica (fecha) um plano de tratamento
   */
  closeTreatmentPlan(treatmentPlanId: string): Promise<ITreatmentPlanDetailed> {
    return new Promise((resolve, reject) => {
      this.http.post<ITreatmentPlanDetailed>(`${this.baseUrl}/${treatmentPlanId}/publish`, {})
        .subscribe({
          next: (plan) => resolve(plan),
          error: (err) => reject(err)
        });
    });
  }

  /**
   * Verifica se um produto existe no plano de tratamento
   * Útil para diagnóstico de erros quando o produto não é encontrado
   */
  checkIfProductExists(treatmentPlanId: string, productId: string): Promise<boolean> {
    return this.getTreatmentPlanById(treatmentPlanId)
      .then(plan => {
        if (!plan || !plan.products) {
          console.error('Plano de tratamento não encontrado ou sem produtos:', treatmentPlanId);
          return false;
        }

        const productExists = plan.products.some(p => p.id === productId);

        if (!productExists) {
          plan.products.forEach(p => {
          });
        }

        return productExists;
      })
      .catch(error => {
        console.error('Erro ao verificar existência do produto:', error);
        return false;
      });
  }

  /**
   * Faz upload de múltiplas fotos para um produto usando o novo endpoint
   * @param treatmentPlanId ID do plano de tratamento
   * @param productId ID do produto
   * @param photos Array de objetos contendo base64 da foto e a ordem
   */
  uploadProductPhotos(
    treatmentPlanId: string,
    productId: string,
    photos: Array<{ photo: string, photo_order: number }>
  ): Promise<ITreatmentPlanPhoto[]> {
    return new Promise<ITreatmentPlanPhoto[]>((resolve, reject) => {
      // DEBUG: Log detalhado de valores de photo_order antes de enviar
      photos.forEach((p, idx) => {
      });

      // Preparar o corpo da requisição conforme a documentação
      const requestBody = {
        photos: photos
      };

      // DEBUG: Log do corpo completo da requisição

      // Fazer a chamada PUT para o novo endpoint
      this.http.put<ITreatmentPlanPhoto[]>(
        `${this.baseUrl}/${treatmentPlanId}/products/${productId}/photos`,
        requestBody
      ).subscribe({
        next: (updatedProduct) => {
          resolve(updatedProduct);
        },
        error: (error) => {
          console.error('Erro ao fazer upload das fotos do produto:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Faz upload de múltiplas fotos de tratamento usando o novo endpoint.
   * @param treatmentPlanId ID do plano de tratamento
   * @param photos Array de objetos contendo base64 da foto, ordem e tipo
   * (PRE_TREATMENT, POST_TREATMENT, MARKING ou IMMEDIATE_POST_TREATMENT)
   * @param type Tipo das fotos a serem enviadas (PRE_TREATMENT, POST_TREATMENT, MARKING ou IMMEDIATE_POST_TREATMENT)
   */
  uploadTreatmentPhotos(
    treatmentPlanId: string,
    photos: Array<{ photo: string, photoOrder: number, type: TreatmentPhotoType }>,
    type: TreatmentPhotoType
  ): Promise<ITreatmentPlanPhoto[]> {
    return new Promise<ITreatmentPlanPhoto[]>((resolve, reject) => {
      // DEBUG: Log detalhado dos dados antes de enviar
      photos.forEach((p, idx) => {
      });

      // Preparar o corpo da requisição conforme a documentação
      const requestBody = {
        photos: photos
      };

      // DEBUG: Log do corpo completo da requisição

      // Fazer a chamada PUT para o endpoint
      this.http.put<ITreatmentPlanPhoto[]>(
        `${this.baseUrl}/${treatmentPlanId}/photos?type=${type}`,
        requestBody
      ).subscribe({
        next: (updatedPlan) => {
          resolve(updatedPlan);
        },
        error: (error) => {
          console.error('Erro ao fazer upload das fotos do tratamento:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Gera URLs de upload presignadas para múltiplos arquivos
   * @param treatmentPlanId ID do plano de tratamento
   * @param productId ID opcional do produto
   * @param fileRequests Array de objetos contendo fileName e contentType
   */
  generatePresignedUrls(treatmentPlanId: string, fileRequests: Array<{ file_name: string, content_type: string }>, productId?: string): Promise<Array<{ uploadUrl: string, objectKey: string, fileName: string }>> {
    return new Promise<Array<{ uploadUrl: string, objectKey: string, fileName: string }>>((resolve, reject) => {
      var url = `${this.baseUrl}/${treatmentPlanId}/generate-presigned-urls`;
      if(productId){
        url += `?productId=${productId}`;
      }
      this.http.post<Array<{ uploadUrl: string, objectKey: string, fileName: string }>>(url, fileRequests).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (error) => {
          console.error('Erro ao gerar URLs de upload presignadas:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Faz upload de um arquivo para o S3
   * @param uploadUrl URL de upload presignada
   * @param file Arquivo a ser enviado
   * @param contentType Tipo de conteúdo do arquivo
   */
  async uploadFileToS3(uploadUrl: string, file: File, contentType: string): Promise<void> {
    // Use native fetch to perform the PUT to the presigned URL so Angular interceptors
    // (Authorization headers, etc.) do not break the S3 presigned request via preflight.
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType || file.type || 'application/octet-stream'
      },
      body: file
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const err = new Error(`Falha no upload com status ${res.status}: ${text}`);
      console.error('Erro ao fazer upload do arquivo:', err);
      throw err;
    }
  }

  /**
   * Deleta uma foto do plano de tratamento
   * @param treatmentPlanId ID do plano de tratamento
   * @param photoId ID da foto
   * @param productId ID opcional do produto
   */
  deletePhoto(treatmentPlanId: string, photoId: string, productId?: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      var url = `${this.baseUrl}/${treatmentPlanId}/photos/${photoId}`;
      if(productId){
        url += `?productId=${productId}`;
      }
      this.http.delete(url).subscribe({
        next: () => resolve(),
        error: (error) => {
          console.error('Erro ao deletar foto:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Cria produto vazio no banco (apenas ID) para ativar QR Code imediatamente
   */
  createQuickProduct(treatmentPlanId: string): Promise<ITreatmentPlanProduct> {
    return new Promise<ITreatmentPlanProduct>((resolve, reject) => {
      const url = `${this.globalConfig.getApiUrl()}/products/create-quick`;
      
      const payload = {
        treatmentPlanId: treatmentPlanId
      };
      
      this.http.post<ITreatmentPlanProduct>(url, payload).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (error) => {
          console.error('Erro ao criar produto vazio:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Exclui produto específico do banco diretamente
   */
  deleteProductDirect(productId: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const url = `${this.globalConfig.getApiUrl()}/products/${productId}`;
      
      this.http.delete<{ success: boolean }>(url).subscribe({
        next: (response) => {
          resolve(response.success);
        },
        error: (error) => {
          console.error('Erro ao excluir produto:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Baixa o PDF do plano de tratamento gerado no backend.
   *
   * O backend monta o PDF via template Thymeleaf + openhtmltopdf a partir dos dados
   * persistidos no banco. O frontend apenas consome o binário.
   *
   * @param treatmentPlanId ID do plano de tratamento.
   * @returns Promise com o Blob PDF e o filename sugerido pelo backend.
   */
  downloadPdf(treatmentPlanId: string): Promise<{ blob: Blob; fileName: string }> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.baseUrl}/${treatmentPlanId}/pdf`, {
        responseType: 'blob',
        observe: 'response'
      }).subscribe({
        next: (response) => {
          const blob = response.body as Blob;
          const disposition = response.headers.get('Content-Disposition') || '';
          const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
          const fileName = match ? decodeURIComponent(match[1].replace(/"/g, '')) : `plano-tratamento-${treatmentPlanId}.pdf`;
          resolve({ blob, fileName });
        },
        error: (error) => {
          console.error('Erro ao baixar PDF do plano de tratamento:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Envia o PDF do plano de tratamento por e-mail.
   *
   * O PDF é gerado internamente no backend — o frontend só precisa informar para
   * quem enviar. Endpoint: {@code POST /treatment-plans/{id}/send-pdf-email} (JSON).
   *
   * @param treatmentPlanId ID do plano de tratamento
   * @param options Opções de envio (destinatários, nome do arquivo, mensagem extra)
   * @returns Promise com a resposta do servidor
   */
  sendPdfByEmail(treatmentPlanId: string, options: ISendPdfEmailOptions = {}): Promise<ISendPdfEmailResponse> {
    return new Promise<ISendPdfEmailResponse>((resolve, reject) => {
      const body: Record<string, unknown> = {};
      if (options.sendToPatient !== undefined) body['sendToPatient'] = options.sendToPatient;
      if (options.sendToClinic !== undefined) body['sendToClinic'] = options.sendToClinic;
      if (options.additionalEmails && options.additionalEmails.length > 0) {
        body['additionalEmails'] = options.additionalEmails;
      }
      if (options.pdfFileName) body['pdfFileName'] = options.pdfFileName;
      if (options.customMessage) body['customMessage'] = options.customMessage;

      this.http.post<ISendPdfEmailResponse>(
        `${this.baseUrl}/${treatmentPlanId}/send-pdf-email`,
        body,
        {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        }
      ).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (error) => {
          console.error('Erro ao enviar PDF por e-mail:', error);
          reject(error);
        }
      });
    });
  }
}